import React, { type ComponentProps, forwardRef, useMemo } from 'react';
import { Platform, StyleSheet, Text, type TextProps, type TextStyle } from 'react-native';

import { FONTS, TYPE_CONFIG, WEIGHT_MAP } from '@garnish/constants';

import { useResponsive } from '../../../hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const DecorativeText = forwardRef<Text, Props>((props, ref) => {
  const {
    children,
    sizeMatch = DEFAULT_SIZE_MATCH,
    italic,
    bold,
    underline,
    style,
    ...restProps
  } = props;

  const { match } = useResponsive();

  // ─── Font Styles ─────────────────────────────────────────────────────

  const fontStyles = useMemo<TextProps['style']>(() => {
    if (bold && italic) {
      return styles.boldItalicText;
    }

    if (bold) {
      return styles.boldText;
    }

    if (italic) {
      return styles.italicText;
    }

    return styles.text;
  }, [bold, italic]);

  // ─── Styles ──────────────────────────────────────────────────────────

  const fontSize = match(sizeMatch);
  const textStyles = TEXT_STYLES[fontSize];
  const androidLineHeightStyles = ANDROID_LINE_HEIGHT_STYLES[fontSize];
  const underlineStyles = underline ? styles.underline : undefined;

  const allStyles = [textStyles, fontStyles, androidLineHeightStyles, underlineStyles, style];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Text ref={ref} style={allStyles} {...restProps}>
      {children}
    </Text>
  );
});

// ─── Constants ───────────────────────────────────────────────────────────────

const TEXT_STYLES = TYPE_CONFIG.DECORATIVE;
const DEFAULT_FONT_SIZE: DecorativeTextFontSize = '36';
const DEFAULT_SIZE_MATCH: SizeMatch = [DEFAULT_FONT_SIZE];

/**
 * Font is rendered differently on Android, so we need to adjust the line height
 * to prevent the text from being cut off.
 */
const ANDROID_LINE_HEIGHT_STYLES: Record<DecorativeTextFontSize, TextStyle | undefined> = {
  '48': Platform.select({ default: undefined, android: { lineHeight: 62 } }),
  '36': Platform.select({ default: undefined, android: { lineHeight: 48 } }),
  '32': Platform.select({ default: undefined, android: { lineHeight: 42 } }),
  '24': Platform.select({ default: undefined, android: { lineHeight: 32 } }),
  '22': Platform.select({ default: undefined, android: { lineHeight: 30 } }),
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  text: {
    fontFamily: FONTS.PERFECTLY_NINETIES_REGULAR,
    fontWeight: WEIGHT_MAP.regular,
  },
  italicText: {
    fontFamily: FONTS.PERFECTLY_NINETIES_REGULAR_ITALIC,
    fontWeight: WEIGHT_MAP.regular,
  },
  boldText: {
    fontFamily: FONTS.PERFECTLY_NINETIES_SEMIBOLD,
    fontWeight: WEIGHT_MAP['semi bold'],
  },
  boldItalicText: {
    fontFamily: FONTS.PERFECTLY_NINETIES_SEMIBOLD_ITALIC,
    fontWeight: WEIGHT_MAP['semi bold'],
  },
  underline: {
    textDecorationLine: 'underline',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  sizeMatch?: SizeMatch;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
} & ComponentProps<typeof Text>;

type SizeMatch = DecorativeTextFontSize[];

type DecorativeTextFontSize = keyof typeof TEXT_STYLES;
